import { ApiProvider, ProviderType, ChatTemplate } from '@/types';

// 示例供应商数据
export const SAMPLE_PROVIDERS: Omit<ApiProvider, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'OpenAI 生产环境',
    baseUrl: 'https://api.openai.com/v1',
    apiKey: 'sk-sample-openai-key-1234567890abcdef',
    providerType: ProviderType.OPENAI,
    models: ['gpt-4-turbo-preview', 'gpt-4', 'gpt-3.5-turbo', 'gpt-3.5-turbo-1106'],
    defaultModel: 'gpt-4-turbo-preview',
    config: {
      temperature: 0.7,
      maxTokens: 2000,
      topP: 1.0,
      frequencyPenalty: 0,
      presencePenalty: 0,
      timeout: 30000,
    },
  },
  {
    name: 'Anthropic Claude',
    baseUrl: 'https://api.anthropic.com',
    apiKey: 'sk-ant-sample-anthropic-key-abcdef123456',
    providerType: ProviderType.ANTHROPIC,
    models: ['claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307', 'claude-2.1'],
    defaultModel: 'claude-3-sonnet-20240229',
    config: {
      temperature: 0.8,
      maxTokens: 4000,
      topP: 0.9,
      frequencyPenalty: 0.2,
      presencePenalty: 0.1,
      timeout: 60000,
    },
  },
  {
    name: 'Google Gemini',
    baseUrl: 'https://generativelanguage.googleapis.com',
    apiKey: 'AIzaSyB-sample-google-key-123456',
    providerType: ProviderType.GOOGLE_AI,
    models: ['gemini-pro', 'gemini-pro-vision'],
    defaultModel: 'gemini-pro',
    config: {
      temperature: 0.9,
      maxTokens: 2048,
      topP: 0.95,
      frequencyPenalty: 0,
      presencePenalty: 0,
      timeout: 45000,
    },
  },
  {
    name: '自定义 API 服务',
    baseUrl: 'https://api.custom-ai-service.com/v1',
    apiKey: 'custom-api-key-abcdef1234567890',
    providerType: ProviderType.CUSTOM,
    models: ['custom-model-1', 'custom-model-2', 'custom-model-pro'],
    defaultModel: 'custom-model-pro',
    config: {
      temperature: 0.5,
      maxTokens: 1000,
      topP: 1.0,
      frequencyPenalty: 0,
      presencePenalty: 0,
      timeout: 10000,
    },
  },
  {
    name: 'Azure OpenAI',
    baseUrl: 'https://your-resource.openai.azure.com',
    apiKey: 'azure-api-key-1234567890abcdef',
    providerType: ProviderType.AZURE_OPENAI,
    models: ['gpt-35-turbo', 'gpt-4', 'gpt-4-32k'],
    defaultModel: 'gpt-35-turbo',
    config: {
      temperature: 0.7,
      maxTokens: 800,
      topP: 1.0,
      frequencyPenalty: 0,
      presencePenalty: 0,
      timeout: 60000,
    },
  },
];

// 更多对话模板
export const ADDITIONAL_TEMPLATES: Omit<ChatTemplate, 'id'>[] = [
  {
    name: '技术问题',
    description: '询问技术问题',
    content: '请解释什么是 RESTful API，并给出一个最佳实践的例子。',
    category: 'code',
    tags: ['技术', 'API', '最佳实践'],
  },
  {
    name: '翻译任务',
    description: '中英文翻译',
    content: '请将以下英文翻译成中文："The quick brown fox jumps over the lazy dog."',
    category: 'creative',
    tags: ['翻译', '语言'],
  },
  {
    name: '数据分析',
    description: '分析数据模式',
    content: '我有以下销售数据：[{"month": "Jan", "sales": 1000}, {"month": "Feb", "sales": 1500}, {"month": "Mar", "sales": 2000}]。请分析增长趋势并给出建议。',
    category: 'test',
    tags: ['数据', '分析', '商业'],
  },
  {
    name: '代码审查',
    description: '审查代码质量',
    content: '请审查以下 JavaScript 代码的质量并提出改进建议：\n```javascript\nfunction processData(data) {\n  let result = [];\n  for (let i = 0; i < data.length; i++) {\n    if (data[i].value > 10) {\n      result.push(data[i]);\n    }\n  }\n  return result;\n}\n```',
    category: 'code',
    tags: ['代码审查', 'JavaScript', '最佳实践'],
  },
  {
    name: '创意头脑风暴',
    description: '创意想法生成',
    content: '请为一家新的环保科技初创公司生成5个创新的产品创意。',
    category: 'creative',
    tags: ['创意', '头脑风暴', '商业'],
  },
  {
    name: '学习计划',
    description: '制定学习计划',
    content: '我想在3个月内学习 React 和 TypeScript，请为我制定一个详细的学习计划。',
    category: 'test',
    tags: ['学习', '计划', '教育'],
  },
  {
    name: '产品描述',
    description: '生成产品描述',
    content: '请为以下产品写一个吸引人的描述：一款智能水杯，可以追踪饮水量、提醒喝水、并保持水温。',
    category: 'creative',
    tags: ['营销', '产品', '写作'],
  },
  {
    name: '错误调试',
    description: '帮助调试错误',
    content: '我在运行以下 Python 代码时遇到了错误，请帮我找出问题：\n```python\ndef calculate_average(numbers):\n    total = sum(numbers)\n    average = total / len(number)\n    return average\n\nprint(calculate_average([1, 2, 3, 4, 5]))\n```',
    category: 'code',
    tags: ['调试', 'Python', '错误'],
  },
];

// 初始化示例数据
export function initializeSampleData() {
  // 延迟导入以避免循环依赖
  import('@/stores/appStore').then(({ useAppStore }) => {
    const { providers, addProvider, addTemplate } = useAppStore.getState();
    
    // 如果还没有供应商，添加示例数据
    if (providers.length === 0) {
      console.log('初始化示例供应商数据...');
      SAMPLE_PROVIDERS.forEach(provider => {
        addProvider(provider);
      });
    }
    
    // 添加额外的模板
    ADDITIONAL_TEMPLATES.forEach(template => {
      addTemplate(template);
    });
    
    console.log('示例数据初始化完成');
  });
}

// 导出示例配置文本
export const SAMPLE_CONFIG_TEXTS = {
  openaiEnv: `OPENAI_API_KEY=sk-abc123def456ghi789jkl012mno345pqr678stu901
OPENAI_BASE_URL=https://api.openai.com/v1
MODEL=gpt-4-turbo-preview
TEMPERATURE=0.7
MAX_TOKENS=2000`,
  
  anthropicJson: `{
  "api": {
    "base_url": "https://api.anthropic.com",
    "api_key": "sk-ant-sample-key-123456",
    "model": "claude-3-opus-20240229"
  },
  "config": {
    "temperature": 0.8,
    "max_tokens": 4000
  }
}`,
  
  googleCode: `const googleConfig = {
  baseUrl: 'https://generativelanguage.googleapis.com',
  apiKey: process.env.GOOGLE_API_KEY,
  model: 'gemini-pro',
  temperature: 0.9,
  maxTokens: 2048
};`,
  
  azureConfig: `# Azure OpenAI Configuration
AZURE_OPENAI_ENDPOINT=https://your-resource.openai.azure.com
AZURE_OPENAI_KEY=azure-key-1234567890abcdef
AZURE_OPENAI_DEPLOYMENT=gpt-35-turbo
AZURE_OPENAI_API_VERSION=2023-12-01-preview`,
  
  mixedFormat: `export API_KEY="sk-mixed-format-key-123456"
export BASE_URL="https://api.custom-service.com/v1"
export MODEL="custom-model-pro"

# Additional settings
TEMPERATURE=0.5
MAX_TOKENS=1000
TOP_P=1.0`,
};